import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { pluralize } from '../shared/utilities/pluralizer.utility'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date | string): string {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function stringToMMMDDYYYY(date: string): string {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function formatDateTime(date: Date | string): string {
  const d = new Date(date)
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Clock time only ("3:42 PM") — e.g. a chat bubble's timestamp (#692). */
export function formatTimeOfDay(date: Date | string | number): string {
  const d = new Date(date)
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function formatRelativeTime(date: Date | string): string {
  const d = new Date(date)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return 'just now'
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${pluralize('minute', diffInMinutes)} ago`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `${diffInHours} ${pluralize('hour', diffInHours)} ago`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return `${diffInDays} ${pluralize('day', diffInDays)} ago`
  }

  const diffInWeeks = Math.floor(diffInDays / 7)
  if (diffInWeeks < 4) {
    return `${diffInWeeks} ${pluralize('week', diffInWeeks)} ago`
  }

  const diffInMonths = Math.floor(diffInDays / 30)
  if (diffInMonths < 12) {
    return `${diffInMonths} ${pluralize('month', diffInMonths)} ago`
  }

  const diffInYears = Math.floor(diffInDays / 365)
  return `${diffInYears} ${pluralize('year', diffInYears)} ago`
}

export function getUrlParam(param: string): string | null {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get(param)
}

/**
 * Sanitizes a string to prevent XSS attacks and other security vulnerabilities
 * Removes HTML tags, script content, and dangerous characters
 * @param input - The string to sanitize
 * @param maxLength - Maximum allowed length (default: 1000)
 * @returns Sanitized string safe for display and processing
 */
export function sanitizeInput(
  input: string,
  maxLength: number = 1000,
  options: { preserveWhitespace?: boolean } = {}
): string {
  if (!input || typeof input !== 'string') return ''

  let sanitized = input

  // Limit length to prevent abuse
  sanitized = sanitized.slice(0, maxLength)

  // Remove null bytes
  sanitized = sanitized.replace(/\0/g, '')

  // Remove HTML tags
  sanitized = sanitized.replace(/<[^>]*>/g, '')

  // Remove script tags and their content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')

  // Remove event handlers (onclick, onerror, etc.)
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
  sanitized = sanitized.replace(/on\w+\s*=\s*[^\s>]*/gi, '')

  // Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, '')

  // Remove data: protocol (can be used for XSS)
  sanitized = sanitized.replace(/data:text\/html/gi, '')

  // Decode common HTML entities to prevent double-encoding attacks
  const htmlEntities: { [key: string]: string } = {
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#x27;': "'",
    '&#x2F;': '/',
    '&amp;': '&',
  }

  Object.entries(htmlEntities).forEach(([entity, char]) => {
    sanitized = sanitized.replace(new RegExp(entity, 'gi'), char)
  })

  // After decoding, remove any HTML that might have been encoded
  sanitized = sanitized.replace(/<[^>]*>/g, '')

  // Remove any remaining control characters except newlines and tabs
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')

  // Trim whitespace
  if (!options.preserveWhitespace) sanitized = sanitized.trim()

  return sanitized
}
